import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { object, string } from 'yup';

// Components
import { H1 } from 'wg-fe-ui';
import LargeButton from '../../../../common/src/components/LargeButton';
import BackButton from '../../../../common/src/components/BackButton';
import IntroAvatar from './IntroAvatar';

// Redux
import { useSelector } from 'react-redux';
import { RootState } from '../../store/redux/reducers/rootReducer';
import FlowerContainer from './FlowerContainer';
import SearchSelectInputV2 from '../../../../common/src/components/SearchSelectInputV2';
import {
  Input,
  Stack,
  FormControl,
  FormHelperText,
  Button,
  Icon,
} from '@wegroup/design-system';
import { parse, stringify } from 'query-string';
import { ReturnOptionProps } from '../../../../../types/ProductFactory';
import { mq } from '../../../../common/src/utils/styledComponentsUtils';
import {
  daysUntilCampaign as daysUntilCampaignFn,
  hasCampaignEnded as hasCampaignEndedFn,
  hasCampaignNotStarted as hasCampaignNotStartedFn,
} from '../../services/productFactoryService';
import {
  isRiskAnalysisFlow,
  useFlowsDispatch,
  useFlowsSelector,
} from '../../../../common/src/utils/globalVariables';
import LogRocket from 'logrocket';
import IntroInfoText from './IntroInfoText';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { isObjectEmpty } from '../../../../claims-admin/src/utils/objectUtils';
import { useLouiseSelector } from '../../store/redux/store';
import { updateAnswers } from '../../../../flows/src/store/redux/slices/flowsSlice';
import languages from '../../constants/languages.json';
import LanguagePicker from './LanguagePicker';
import BrokerInfoModal from '../../../../flows/src/components/ui/advisory-flow/components/broker-info/BrokerInfoModal';
import useTempLanguage from '../../../../flows/src/hooks/useTempLanguage';
import useIsNLplan from '../../../../common/src/hooks/useIsNLPlan';
import useAuth from '../../../../app/src/features/authentication/hooks/useAuth';
import { useLocale } from '../../../../../i18n';
import {
  isSocialLoginUrl,
  isSocialNLDomain,
} from '../../../../app/src/features/authentication/constants/SocialAuth';
import { usePostHog } from 'posthog-js/react';

interface Props {
  isLoadingCaptcha: boolean;
  displayName: string;
  isSocial: boolean;
  buttonAction: () => void;
  isCampaignValid?: boolean;
  campaignStartDate?: string;
  campaignEndDate?: string;
}

const IntroContainer: React.FC<React.PropsWithChildren<Props>> = ({
  isLoadingCaptcha,
  displayName,
  isSocial,
  buttonAction,
  campaignStartDate,
  campaignEndDate,
  isCampaignValid,
}) => {
  const flowsDispatch = useFlowsDispatch();

  const { isNL } = useIsNLplan();
  const { t } = useTranslation();
  const history = useHistory();
  const [showModal, setShowModal] = useState<boolean>(false);
  const answers = useFlowsSelector((state) => state.flows.answers);
  const { user, distribution } = useAuth();
  const prefilledSessionId = useLouiseSelector(
    (state) => state.selection.prefilledSessionId,
  );
  const { tempLanguage, setTempLanguage } = useTempLanguage();
  const { userLang } = useLocale();
  const posthog = usePostHog();

  const campaign = useSelector(
    (state: RootState) => state.productFactory.campaign,
  );

  const location = useLocation();
  const params = parse(location.search);
  const prefilledEmail = answers.find(
    ({ id }) => id === 'policy_holder.email',
  )?.answer;
  const prefilledLanguage: string = answers.find(
    ({ id }) => id === 'policy_holder.language',
  )?.answer as string;

  const hasCampaignNotStarted = hasCampaignNotStartedFn(campaignStartDate);
  const hasCampaignEnded = hasCampaignEndedFn(campaignEndDate);
  const daysUntilCampaign = daysUntilCampaignFn(campaignStartDate);

  const getDesc = () => {
    if (!isObjectEmpty(campaign) && !campaign.active) {
      return t('ADVISORY-CONVERSATION_INTRO_INACTIVE-CAMPAIGN');
    }
    if (!isCampaignValid)
      return t('I can not find the campaign you are looking for');
    if (hasCampaignEnded)
      return t(
        'Unfortunately, this campaign has expired However, you can still contact your broker in case of further interest',
      );
    if (hasCampaignNotStarted)
      return (
        t('Unfortunately this campaign hasnt started yet the campaign will') +
        (daysUntilCampaign === 1
          ? t('start tomorrow')
          : t('start in', {
              daysLeft: daysUntilCampaign,
            }))
      );
    if (isRiskAnalysisFlow())
      return t('ADVISORY-CONVERSATION_INTRO_DESC_RISK-ANALYSIS', {
        distribution: displayName,
      });
    return t(
      'Welcome to X I will help you choose the right insurance for your needs and guide you through filling out the necessary details',
      { distribution: displayName },
    );
  };

  const schema = object().shape({
    email: string()
      .email('Login.errors.email')
      .required('required')
      .max(100, 'too long')
      .matches(
        /^[a-zA-Z0-9.+\-!#$%&'*/=?^_`{|}~]{1,64}@[a-zA-Z0-9-.]{1,63}\.[a-zA-Z0-9-]{1,62}[a-zA-Z0-9]$/,
        'incorrect format',
      ),
  });

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<{ email: string }>({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
    defaultValues: {
      email: (prefilledEmail as string) || '',
    },
  });

  const onSubmit = (data: { email: string }) => {
    flowsDispatch(
      updateAnswers({
        id: 'policy_holder.email',
        answer: data.email,
        groupId: isRiskAnalysisFlow()
          ? 'policy_holder.personal_information'
          : 'policy_holder.contact',
        prefilled: true,
      }),
    );

    if (data.email && isSocialLoginUrl) {
      LogRocket.identify('Email Login', {
        email: data.email,
      });
      posthog.setPersonProperties({
        email: data.email,
      });
      LogRocket.track('CampaignUser', { email: getValues().email as string });
    }

    buttonAction();
  };

  const handleOnButtonClick = () => {
    if (isSocial && !prefilledSessionId) handleSubmit(onSubmit)();
    else {
      buttonAction();
    }
  };

  useEffect(() => {
    if (prefilledLanguage) {
      const newQueryParams = { ...params };
      newQueryParams.lang = prefilledLanguage?.toLowerCase();
      history.replace({
        pathname: location.pathname,
        search: stringify(newQueryParams),
      });
    }
    if (!isSocialNLDomain) {
      if (isRiskAnalysisFlow() && isNL) {
        setTempLanguage('nl-NL');
      } else if (!tempLanguage) {
        setTempLanguage(getLanguageValue()?.value || 'EN');
      }
    }
  }, []);

  const onLanguageChange = (e: ReturnOptionProps) => {
    const newQueryParams = { ...params };
    newQueryParams.lang = (e.value as string)?.toLowerCase();
    flowsDispatch(
      updateAnswers({
        id: 'policy_holder.language',
        answer: e.value,
        groupId: 'policy_holder.natural_info',
        prefilled: true,
      }),
    );
    history.replace({
      pathname: location.pathname,
      search: stringify(newQueryParams),
    });
  };

  const getLanguageValue = () =>
    languages.find(({ value }) => {
      if (prefilledLanguage) {
        return prefilledLanguage.toLowerCase() === value.toLowerCase();
      } else if (params?.lang && typeof params.lang === 'string') {
        return value.toLowerCase() === params.lang?.toLowerCase();
      } else if (isSocialLoginUrl) {
        return campaign?.language?.toLowerCase() === value.toLowerCase();
      }
      return userLang.toLowerCase() === value.toLowerCase();
    });

  const handleOnBrokerInfoClick = () => {
    setShowModal(true);
  };

  return (
    <FlowerContainer maxWidth="48rem" data-test-id="ProdFac_IntroContainer">
      <LanguagePicker />
      <BrokerInfoModal showModal={showModal} setShowModal={setShowModal} />
      <IntroAvatar isSocial={isSocial} />
      <StyledH1 data-test-id="ProdFac_IntroContainer_Title">
        {t("Hello, I'm", {
          assistant:
            campaign.assistant_name ||
            user?.assistant_name ||
            distribution.assistant_name ||
            'Louise',
        })}
      </StyledH1>
      <Description data-test-id="ProdFac_IntroContainer_Description_1">
        {getDesc()}
      </Description>
      {isCampaignValid &&
      !hasCampaignNotStarted &&
      !hasCampaignEnded &&
      (isObjectEmpty(campaign) || campaign.active) ? (
        <>
          {isSocial && !prefilledSessionId && (
            <>
              <Stack direction={{ base: 'column', mobileL: 'row' }} spacing="4">
                <FormControl>
                  {/* Custom height so that it fits with the search input */}
                  <Input
                    h="4.5rem"
                    placeholder={t('Email')}
                    {...register('email')}
                    data-test-id="ADVISORY-CONVO_INTRO-VIEW_INPUT_EMAIL"
                  />
                  {errors.email?.message && (
                    <FormHelperText color="danger.main">
                      {t(errors.email?.message)}
                    </FormHelperText>
                  )}
                </FormControl>
                {!isNL && (
                  <StyledSearchSelectInputV2
                    name="language"
                    onSelected={onLanguageChange}
                    value={getLanguageValue()}
                    options={languages}
                    menuPlacement="top"
                    cacheOptions={false}
                  />
                )}
              </Stack>
              <IntroInfoText />
            </>
          )}
          <Button
            mt="6"
            py="6"
            w="100%"
            isLoading={isLoadingCaptcha}
            onClick={handleOnButtonClick}
            rightIcon={<Icon name="GeneralActionsArrowRight" />}
            borderRadius="full"
            data-test-id="ProdFac_LargeButton"
          >
            <p>{t('Okay, let’s get started')}</p>
          </Button>
        </>
      ) : (
        isCampaignValid && (
          <StyledLargeButton onClick={handleOnBrokerInfoClick} fullwidth>
            {t('Broker information')}
          </StyledLargeButton>
        )
      )}
      {!isSocial && (
        <BackButton onClick={() => history.push('/dashboard')}>
          {t('Go back to dashboard')}
        </BackButton>
      )}
    </FlowerContainer>
  );
};

const StyledSearchSelectInputV2 = styled(SearchSelectInputV2)`
  width: 100%;

  ${mq.mobileL} {
    width: 75%;
  }
`;

const StyledLargeButton = styled(LargeButton)`
  margin-top: 2.4rem;
`;

const Description = styled.p`
  line-height: 125%;
  text-align: center;
  font-size: 1.4rem;
  margin-bottom: 2.4rem;
  color: ${({ theme }) => theme.gray[600]};
  ${mq.tablet} {
    font-size: 1.7rem;
  }
`;

const StyledH1 = styled(H1)`
  margin: 5.6rem 0 1.6rem;
  margin-top: 2.4rem;
  text-align: center;
  line-height: 120%;
  font-size: 2.4rem;

  ${mq.tablet} {
    font-size: 3.6rem;
  }
`;

export default IntroContainer;
